import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { decryptToken } from '../../../Utlis/Encryption';
import AbsenceAPI from '../../../services/absenceApi';
import { toast } from 'react-hot-toast';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';

const AbsenceList = () => {
  const { t } = useTranslation();
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    absenceType: '',
  });

  const rawEncryptedToken = localStorage.getItem('token');
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    if (token) {
      loadAbsences();
    }
  }, [pagination.page, filters]);

  const loadAbsences = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const api = new AbsenceAPI(token);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.startDate && { start_date: filters.startDate }),
        ...(filters.endDate && { end_date: filters.endDate }),
        ...(filters.absenceType && { absence_type: filters.absenceType }),
      };

      const response = await api.getMyAbsences(params);
      console.log("response", response);

      // Handle both response formats: status === 'success' or error === false
      if (response.status === 'success' || (response.error === false && response.data)) {
        const absencesData = response.data?.absences || response.data || [];
        setAbsences(Array.isArray(absencesData) ? absencesData : []);
        
        if (response.data?.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.data.pagination,
          }));
        }
      }
    } catch (error) {
      console.error('Error loading absences:', error);
      toast.error(error.message || 'Failed to load absences');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      absenceType: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getAbsenceTypeColor = (type) => {
    return type?.color || '#9CA3AF';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle ISO date strings with time
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  const absenceTypes = [
    { code: 'VACATION', label: 'Vacation' },
    { code: 'MEDICAL', label: 'Medical Leave' },
    { code: 'PERSONAL', label: 'Personal Leave' },
    { code: 'UNJUSTIFIED', label: 'Unjustified Absence' },
    { code: 'PAID_LEAVE', label: 'Paid Leave' },
    { code: 'UNPAID_LEAVE', label: 'Unpaid Leave' },
    { code: 'TRAINING', label: 'Training' },
    { code: 'OTHER', label: 'Other' },
  ];

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
<LocalizationProvider dateAdapter={AdapterDayjs}>
  {/* Start Date */}
  <DatePicker
    label={t("absences.filters.startDate")}
    value={filters.startDate ? dayjs(filters.startDate) : null}
    onChange={(newValue) =>
      handleFilterChange(
        "startDate",
        newValue ? newValue.format("YYYY-MM-DD") : ""
      )
    }
    slotProps={{
      textField: {
        size: "small",
        InputProps: {
          sx: {
            height: 36,                 // ✅ KEEP THIS
            fontSize: "12px",
            boxSizing: "border-box",
            "& input": {
              padding: "6px 8px",
              fontSize: "12px",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 18,
            },
          },
        },
        InputLabelProps: {
          sx: {
            fontSize: "12px",
          },
        },
        sx: {
          minWidth: 180,
        },
      },

      // ✅ CALENDAR POPUP FIX (MONTH / YEAR SIZE)
      popper: {
        sx: {
          "& .MuiPickersCalendarHeader-label": {
            fontSize: "13px",           // 🔽 Month + Year
            fontWeight: 500,
          },
          "& .MuiDayCalendar-weekDayLabel": {
            fontSize: "11px",
          },
          "& .MuiPickersDay-root": {
            fontSize: "12px",
          },
        },
      },
    }}
  />

  {/* End Date */}
  <DatePicker
    label={t("absences.filters.endDate")}
    value={filters.endDate ? dayjs(filters.endDate) : null}
    onChange={(newValue) =>
      handleFilterChange(
        "endDate",
        newValue ? newValue.format("YYYY-MM-DD") : ""
      )
    }
    slotProps={{
      textField: {
        size: "small",
        InputProps: {
          sx: {
            height: 36,                 // ✅ SAME HEIGHT
            fontSize: "12px",
            boxSizing: "border-box",
            "& input": {
              padding: "6px 8px",
              fontSize: "12px",
            },
            "& .MuiSvgIcon-root": {
              fontSize: 18,
            },
          },
        },
        InputLabelProps: {
          sx: {
            fontSize: "12px",
          },
        },
        sx: {
          minWidth: 180,
        },
      },

      popper: {
        sx: {
          "& .MuiPickersCalendarHeader-label": {
            fontSize: "13px",
            fontWeight: 500,
          },
          "& .MuiDayCalendar-weekDayLabel": {
            fontSize: "11px",
          },
          "& .MuiPickersDay-root": {
            fontSize: "12px",
          },
        },
      },
    }}
  />
</LocalizationProvider>






          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ fontSize: '12px' }}>{t("absences.filters.absenceType")}</InputLabel>
            <Select
              value={filters.absenceType}
              onChange={(e) => handleFilterChange('absenceType', e.target.value)}
              label="Absence Type"
              sx={{ fontSize: '12px', '& .MuiSelect-select': { fontSize: '12px' } }}
            >
              <MenuItem value="" sx={{ fontSize: '12px' }}>All Types</MenuItem>
              {absenceTypes.map((type) => (
                <MenuItem key={type.code} value={type.code} sx={{ fontSize: '12px' }}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton onClick={clearFilters} color="primary">
            <ClearIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
                <TableCell sx={{ fontWeight: 600, color: '#44546F', fontSize: '12px', minWidth: 150 }}>{t("absences.table.type")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#44546F', fontSize: '12px', minWidth: 180 }}>{t("absences.table.dateRange")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#44546F', fontSize: '12px', minWidth: 200 }}>{t("absences.table.comment")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#44546F', fontSize: '12px', minWidth: 130 }}>{t("absences.table.source")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#44546F', fontSize: '12px', minWidth: 120 }}>{t("absences.table.created")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : absences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary" sx={{ fontSize: '12px' }}>
                      {t('absence.noAbsences')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                absences.map((absence) => (
                  <TableRow key={absence.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: getAbsenceTypeColor(absence.type),
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>
                          {absence.type?.name || 'N/A'}
                        </Typography>
                        {absence.type?.isPaid && (
                          <Chip
                            label={t("absences.badges.paid")}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '10px',
                              backgroundColor: '#E8F5E9',
                              color: '#2E7D32',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {absence.dateRange?.start 
                          ? formatDate(absence.dateRange.start) 
                          : 'N/A'} - {absence.dateRange?.end 
                          ? formatDate(absence.dateRange.end) 
                          : 'N/A'}
                      </Typography>
                      {absence.isPartial && absence.partialTimes && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '11px', display: 'block' }}>
                          Partial: {absence.partialTimes.start || 'N/A'} - {absence.partialTimes.end || 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 300, 
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={absence.comment || ''}
                      >
                        {absence.comment || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                    <Chip
  label={t(
    `absences.source.${absence.source}`,
    absence.source
      ? absence.source
          .replace(/_/g, " ")
          .replace(/\b\w/g, c => c.toUpperCase())
      : t("absences.states.notAvailable")
  )}
  size="small"
  sx={{
    backgroundColor: "#F5F7FA",
    color: "#44546F",
    fontSize: "11px",
  }}
/>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {absence.createdAt ? dayjs(absence.createdAt).format('MMM DD, YYYY') : 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && absences.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(event, value) => setPagination(prev => ({ ...prev, page: value }))}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AbsenceList;
